import {Component, h} from "preact";
import DefaultFallback from "./DefaultFallback";

/**
 * Error boundary — isolates render failures so a crash in one subtree
 * degrades locally instead of blanking the whole SPA.
 *
 * Class component by design: Preact only exposes error capture through
 * the componentDidCatch/getDerivedStateFromError lifecycle — there is no
 * hook equivalent, so the view/controller split does not apply here.
 *
 * `fallback` contract:
 *   - undefined      → renders the DefaultFallback (message + retry + home)
 *   - null           → renders nothing (silent degradation, decorative blocks)
 *   - vnode          → rendered as given
 *   - (retry) => vnode → rendered with the retry callback wired in
 */
class ErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = {error: null, resetKey: props.resetKey};
        this.handleRetry = this.handleRetry.bind(this);
    }

    static getDerivedStateFromError(error) {
        return {error};
    }

    static getDerivedStateFromProps(props, state) {
        // A route change must clear a captured error, otherwise the fallback
        // from a crashed page sticks across client-side navigation.
        if (props.resetKey !== state.resetKey) {
            return {resetKey: props.resetKey, error: null};
        }

        return null;
    }

    componentDidCatch(error, errorInfo) {
        // Never swallow silently — the boundary contains the crash, it must
        // not hide it. Telemetry plugs in through onError (separate ticket).
        console.error(
            `[ErrorBoundary:${this.props.scope || "unscoped"}]`,
            error,
            errorInfo
        );

        if (typeof this.props.onError === "function") {
            this.props.onError(error, errorInfo, this.props.scope);
        }
    }

    handleRetry() {
        // The crashed subtree was unmounted when the fallback rendered, so
        // clearing the error remounts it from scratch (fresh state).
        this.setState({error: null});
    }

    render() {
        const {children, fallback} = this.props;
        const {error} = this.state;

        if (error) {
            if (typeof fallback === "function") {
                return fallback(this.handleRetry);
            }

            if (fallback !== undefined) {
                return fallback;
            }

            return <DefaultFallback onRetry={this.handleRetry} />;
        }

        return children;
    }
}

/**
 * Wraps a component's export so every consumer gets the boundary for free.
 * Used on high-reuse components (product cards, badges) where protecting
 * each call site individually would not scale.
 */
export const withErrorBoundary = (Wrapped, boundaryProps = {}) => {
    const WithBoundary = props => (
        <ErrorBoundary {...boundaryProps}>
            <Wrapped {...props} />
        </ErrorBoundary>
    );

    return WithBoundary;
};

export default ErrorBoundary;
